import { useEffect, useState } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { haalWieBenIk } from '../lib/api.js';
import { setTokenOphaler } from './huidigeGebruiker.js';
import { laadVanServer } from '../lib/serverSync.js';
import { flushLogsNu } from '../lib/logSync.js';
import { isRoosterbaarVolgensRollen } from '../lib/rollen.js';
import { loginRequest } from './msalConfig.js';
import { leesActieveOmgeving, zetActieveOmgeving, kiesActieveOmgeving } from '../lib/omgeving.js';

// Speling waarmee we een ID-token al als "verlopen" behandelen: liever één ronde te veel
// dan een token dat onderweg net over de datum gaat.
const VERVERS_MARGE_MS = 5 * 60 * 1000;

/**
 * Verloopt het ID-token in dit MSAL-resultaat binnen de marge?
 *
 * Nodig omdat MSAL beslist óf hij ververst op basis van de ACCESS token voor de gevraagde
 * scopes (User.Read → Microsoft Graph), terwijl wij het ID-TOKEN naar onze server sturen.
 * Een cache-hit op de access token kan dus een al verlopen ID-token teruggeven — de server
 * antwoordt dan met "Ongeldig token: exp claim timestamp check failed". Daarom checken we
 * de exp van het ID-token zelf en forceren we zo nodig een verse ronde.
 *
 * exp is in SECONDEN (JWT-standaard). Geen exp bekend → als bijna-verlopen behandelen,
 * dan halen we liever onnodig een vers token op dan een ongeldig te versturen.
 */
function idTokenBijnaVerlopen(res) {
  const exp = res?.idTokenClaims?.exp;
  if (typeof exp !== 'number') return true;
  return exp * 1000 < Date.now() + VERVERS_MARGE_MS;
}

/**
 * Centrale rol-hook. Leest het ingelogde account (MSAL) en resolvet via de server
 * de gekoppelde medewerker + rol. Eén bron van waarheid voor de rol-bewuste nav (7c).
 *
 * Statussen:
 *   status = 'uit'      -> niet ingelogd
 *   status = 'laden'    -> ingelogd, wie-ben-ik loopt nog
 *   status = 'klaar'    -> resolved (gekoppeld true/false bekend)
 *   status = 'fout'     -> server onbereikbaar / fout
 *
 * Bij gekoppeld=false is de gebruiker wel ingelogd maar niet aan een medewerker
 * gekoppeld (rol valt dan terug op 'medewerker' zodat de nav niet leeg blijft).
 */
export function useRol() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = instance.getActiveAccount() ?? accounts[0] ?? null;
  const email = account?.username ?? null;

  const [state, setState] = useState({ status: 'uit', rol: null, mwKey: null, naam: null, gekoppeld: false, rollen: null, omgevingen: [], actieveOmgeving: null });

  useEffect(() => {
    // Registreer een token-ophaler die bij elke beschermde call een vers token levert.
    // Kan MSAL niet meer stil verversen (refresh-token weg/verlopen), dan gooit
    // acquireTokenSilent een InteractionRequiredAuthError. Die vangen we hier BEWUST niet op:
    // haalVersToken() herkent 'm en meldt de sessie centraal als verlopen (overlay in App).
    if (isAuthenticated && account) {
      setTokenOphaler(async () => {
        let res = await instance.acquireTokenSilent({ ...loginRequest, account });
        if (idTokenBijnaVerlopen(res)) {
          res = await instance.acquireTokenSilent({ ...loginRequest, account, forceRefresh: true });
        }
        return res.idToken ?? null;
      });
      // Nu er (weer) een token beschikbaar is: opgespaarde log-entries alsnog doorsturen
      // (login-scherm-fouten zonder token blijven zo niet hangen). Minimaal, fire-and-forget.
      flushLogsNu();
    } else {
      setTokenOphaler(null);
    }
    if (!isAuthenticated || !email) {
      // Blijft bewust staan: dit IS de overgang ingelogd → uitgelogd. Zonder deze reset houdt
      // status 'klaar' met rol teamleider, mwKey en omgevingen, en toont App.jsx de
      // teamleider-nav aan wie net is uitgelogd. Bij een koude mount zonder login is dit object
      // gelijk aan de initiële useState-waarde en kost het enkel een extra render — dát signaleert
      // de regel, en dat weegt niet op tegen het risico van de reset weghalen.
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState({ status: 'uit', rol: null, mwKey: null, naam: null, gekoppeld: false, rollen: null, omgevingen: [], actieveOmgeving: null });
      return;
    }
    let afgebroken = false;
    setState((s) => ({ ...s, status: 'laden' }));
    haalWieBenIk(email)
      .then((res) => {
        if (afgebroken) return;
        if (res?.gekoppeld) {
          const rol = res.rol ?? 'medewerker';
          // Omgevingen uit de respons; een oude server (zonder omgevingen-veld) → val terug
          // op een 1-item-lijst uit de top-level velden, zodat er niets breekt.
          const omgevingen = Array.isArray(res.omgevingen) && res.omgevingen.length
            ? res.omgevingen
            : [{ sleutel: 'arnhem_eo', mwKey: res.mwKey, naam: res.naam, rollen: res.rollen, rol }];
          // Bron van waarheid voor de actieve lade: localStorage (nu nog impliciet arnhem_eo).
          const actieveOmgeving = kiesActieveOmgeving(omgevingen, leesActieveOmgeving());
          zetActieveOmgeving(actieveOmgeving);
          setState({ status: 'klaar', rol, mwKey: res.mwKey, naam: res.naam, gekoppeld: true, rollen: res.rollen, omgevingen, actieveOmgeving });
          if (rol === 'teamleider') laadVanServer();
        } else {
          setState({ status: 'klaar', rol: 'medewerker', mwKey: null, naam: null, gekoppeld: false, rollen: null, omgevingen: [], actieveOmgeving: null });
        }
      })
      .catch(() => {
        if (afgebroken) return;
        setState({ status: 'fout', rol: null, mwKey: null, naam: null, gekoppeld: false, rollen: null, omgevingen: [], actieveOmgeving: null });
      });
    return () => { afgebroken = true; };
    // account en instance staan hier bewust NIET in de deps. instance komt uit de MsalProvider
    // en is stabiel voor de levensduur van de app. account wordt bovenin de hook elke render
    // opnieuw berekend en heeft geen stabiele identiteit; toevoegen kan de effect laten
    // herdraaien en daarmee setTokenOphaler, flushLogsNu en haalWieBenIk in een lus brengen —
    // precies in de loginflow. email is uit account afgeleid en vangt een gebruikerswissel al
    // af; acquireTokenSilent matcht op homeAccountId, dat per gebruiker stabiel is, dus een
    // verouderd account-object van dezelfde gebruiker lost nog steeds correct op.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, email]);

  return { ...state, email, isTeamleider: state.rol === 'teamleider', isRoosterbaar: isRoosterbaarVolgensRollen(state.rollen) };
}
